import CustomModalWrapper from 'components/CustomModalWrapper/CustomModalWrapper';
import DetailTable from 'components/DetailTable';
import { ConfigItem } from 'components/DetailTable/types';
import LoadingScreen from 'components/LoadingScreen';
import { useAtomValue } from 'jotai';
import React, { Fragment, useEffect, useState } from 'react';
import { Row, Col, Button, ButtonGroup } from 'reactstrap';
import AccountInfoSummary from 'views/sast2/components/AccountInfoSummary';
import { companyIdAtom } from 'views/state';

import { fetchAccountDetail, processJsonData } from '../helpers';

type Props = {
  id: number;
  isActive?: boolean;
};

const AccountDetailModalContent = ({ id, isActive }: Props) => {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const companyIdValue = useAtomValue(companyIdAtom);

  const generalTabConfig: ConfigItem[] = [
    { name: 'Id', key: 'id', type: 'string' },
    { name: 'Title', key: 'title', type: 'string' },
    { name: 'Active', key: 'is_active', type: 'boolean' },
    { name: 'Type', key: 'host_type', type: 'string' },
    {
      name: 'Owner',
      key: 'owner',
      type: 'object',
      config: [
        { name: 'Platform Account', key: 'name', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
        { name: 'Platform Company', key: 'company', type: 'string' },
      ],
    },
    {
      name: 'Connected By',
      key: 'user',
      type: 'object',
      config: [
        { name: 'Full Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
    {
      name: 'Last Reactivated By',
      key: 'reactivated_by',
      type: 'object',
      config: [
        { name: 'Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
    { name: 'Last Reactivated At', key: 'reactivated_at', type: 'date' },
    {
      name: 'Last Deactivated By',
      key: 'deactivated_by',
      type: 'object',
      config: [
        { name: 'Name', key: 'fullname', type: 'string' },
        { name: 'Email', key: 'email', type: 'string' },
      ],
    },
    { name: 'Last Deactivated At', key: 'deactivated_at', type: 'date' },
    { name: 'Created At', key: 'created_at', type: 'date' },
    { name: 'Updated At', key: 'updated_at', type: 'date' },
  ];

  const repositoriesTabConfig: ConfigItem[] = [
    {
      name: '',
      key: 'repositories',
      type: 'list',
      listConfig: {
        childNode: undefined,
        config: [
          // { name: 'Id', key: 'id', type: 'string' },
          { name: 'Name', key: 'name', type: 'string' },
          { name: 'Branch', key: 'branch', type: 'string' },
          { name: 'Active', key: 'is_active', type: 'string' },
          { name: 'Status', key: 'status', type: 'string' },
        ],
      },
    },
  ];

  const connectivityTabConfig: ConfigItem[] = [
    { name: 'Status', key: 'token_info.status', type: 'string' },
    {
      name: 'Failed Attempts',
      key: 'token_info.access_token_failed_attempts',
      type: 'string',
    },
    {
      name: 'Failed Reason',
      key: 'token_info.access_token_failed_reason',
      type: 'string',
    },
  ];

  // tabs is the configuration for the tabs which are rendered
  const tabs = [
    {
      title: 'General Info',
      config: generalTabConfig,
      data: data,
    },
    {
      title: 'Repositories',
      config: repositoriesTabConfig,
      data: data,
    },
    {
      title: 'Connectivity Status',
      config: connectivityTabConfig,
      data: data,
    },
  ];

  useEffect(() => {
    if (companyIdValue && companyIdValue !== -1) {
      fetchAccountDetail(id, companyIdValue)
        .then((res) => {
          if (res) {
            const processedJson = processJsonData(res);
            setData(processedJson);
          }
        })
        .catch((err) => {
          throw err;
        })
        .finally(() => setIsLoading(false));
    }
  }, [id, companyIdValue, isActive]);

  if (isLoading) return <LoadingScreen />;

  return (
    <CustomModalWrapper overflowY="hidden" id="account-detail">
      {data && (
        <Fragment>
          <Row>
            <Col sm="6" md="4" lg="3">
              <ButtonGroup
                vertical
                className="btn-group-toggle"
                style={{ maxWidth: '100%', width: '100%' }}
              >
                {tabs.map((tab, index) => (
                  <Button
                    key={`tab-btn-${index}`}
                    tag="label"
                    style={{
                      padding: '10px',
                      margin: '0px',
                      textAlign: 'left',
                    }}
                    color={activeTab === index ? 'primary' : 'secondary'}
                    size="sm"
                    title={tab.title}
                    onClick={() => setActiveTab(index)}
                  >
                    {tab.title}
                  </Button>
                ))}
              </ButtonGroup>
            </Col>
            <Col sm="12" md="8" lg="9">
              <CustomModalWrapper id="account-detail-scroll">
                <Row>
                  <Col sm="12">
                    {data ? (
                      <AccountInfoSummary
                        props={data}
                        description={undefined}
                        mode={'normal'}
                      />
                    ) : (
                      <></>
                    )}
                  </Col>
                </Row>
                {tabs.map(
                  (tab, index) =>
                    activeTab === index && (
                      <DetailTable
                        key={index}
                        data={tab.data}
                        config={tab.config}
                      />
                    ),
                )}
              </CustomModalWrapper>
            </Col>
          </Row>
        </Fragment>
      )}
    </CustomModalWrapper>
  );
};

export default AccountDetailModalContent;
